import { onMounted, onUnmounted, watch } from 'vue'
import type { Ref } from 'vue'

import { usePriorityPaymentsStore } from '@/pinia-store/usePriorityPaymentsStore'

/** Mesma cadência do painel Pós-plantão (Q9). */
export const PRIORITY_PAYMENTS_REFRESH_INTERVAL_MS = 60_000

/**
 * Mantém a fila viva enquanto a aba fica aberta, sem atrapalhar quem está no
 * meio de uma ação.
 *
 * @param isPaused Modal aberto ou ataque em curso. Trocar as linhas debaixo do
 *   painel de ataque mudaria o plantão que a pessoa está cobrando — o refresh
 *   fica represado e é aplicado quando ela sai.
 */
export function usePriorityPaymentsAutoRefresh(isPaused: Ref<boolean>): void {
  const store = usePriorityPaymentsStore()

  let timer: ReturnType<typeof setInterval> | null = null
  let isRefreshPending = false

  const canRefresh = (): boolean =>
    !isPaused.value &&
    document.visibilityState === 'visible' &&
    !store.isLoading &&
    !store.isRefetching

  async function flushPendingRefresh(): Promise<void> {
    if (!isRefreshPending || !canRefresh()) return
    isRefreshPending = false
    // Silencioso: o esqueleto piscando a cada minuto atrapalharia a leitura.
    await store.fetchList({ silent: true })
  }

  function tick(): void {
    isRefreshPending = true
    void flushPendingRefresh()
  }

  const stopPauseWatcher = watch(isPaused, (paused) => {
    if (!paused) void flushPendingRefresh()
  })

  const handleVisibilityChange = (): void => {
    if (document.visibilityState !== 'visible') return
    // Aba que passou meia hora em segundo plano volta atualizada em vez de
    // mostrar um prazo vencido como se ainda houvesse tempo.
    tick()
  }

  onMounted(() => {
    timer = setInterval(tick, PRIORITY_PAYMENTS_REFRESH_INTERVAL_MS)
    document.addEventListener('visibilitychange', handleVisibilityChange)
  })

  onUnmounted(() => {
    if (timer) clearInterval(timer)
    timer = null
    document.removeEventListener('visibilitychange', handleVisibilityChange)
    stopPauseWatcher()
  })
}
